<script setup>
  import { ref, computed } from "vue";
  import { storeToRefs } from "pinia";
  import { Upload, MapPin, X } from "lucide-vue-next";
  import { Button } from "@/components/ui/button";
  import { Input } from "@/components/ui/input";
  import { Textarea } from "@/components/ui/textarea";
  import { Tooltip } from "@/components/ui/tooltip";
  import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
  import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
  import { LabeledField } from "@/components/episto-ui/labeled-field";
  import { useLocationsStore } from "@stores/mAudience/locations";
  import { isEmpty, uniq } from "@utils/toolBox";

  const locationsStore = useLocationsStore();
  const { addLocationsFacebook, addLocationsGoogle, resetParams } = locationsStore;
  const { bulkAddParams } = storeToRefs(locationsStore);

  const dialogVisible = ref(false);

  const displayDialog = () => (dialogVisible.value = true);
  const resetAndCloseDialog = () => {
    resetParams();
    dialogVisible.value = false;
  };
  const updateParamsType = (value) => {
    bulkAddParams.value.type = value;
    if (hasRadius.value) bulkAddParams.value.radius = getMin.value;
  };
  const createRadius = () => {
    bulkAddParams.value.has_radius = true;
    bulkAddParams.value.radius = getMin.value;
  };
  const removeRadius = () => (bulkAddParams.value.has_radius = false);
  const handleBulkAddLocations = async () => {
    const locationString = bulkAddParams.value.locations;
    if (!locationString) resetAndCloseDialog();

    const locationsArray = locationString.split(/\n+/);
    const formattedLocations = uniq(
      locationsArray
        .map((text) => text.replace(/^[\W]+\s*/g, ""))
        .filter((text) => !isEmpty(text) && !isEmpty(text.replace(/\s/, ""))),
    );

    dialogVisible.value = false;

    if (isEmpty(formattedLocations)) return;
    if (bulkAddParams.value.type === "city") await addLocationsFacebook(formattedLocations);
    else await addLocationsGoogle(formattedLocations);
  };

  const hasRadius = computed(() => bulkAddParams.value.has_radius);
  const getMin = computed(() => (bulkAddParams.value.type === "city" ? 17 : 1));
</script>

<template>
  <Tooltip content="Bulk add locations">
    <Button
      variant="default"
      size="icon"
      @click="displayDialog">
      <Upload class="h-4 w-4" />
    </Button>
  </Tooltip>
  <Dialog
    :open="dialogVisible"
    @update:open="dialogVisible = $event">
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Bulk add locations</DialogTitle>
      </DialogHeader>
      <div class="space-y-4">
        <RadioGroup
          :model-value="bulkAddParams.type"
          @update:model-value="updateParamsType">
          <div class="flex items-center gap-4">
            <label class="flex items-center gap-2">
              <RadioGroupItem value="city" />
              <span class="text-sm">City / Zipcode / Region</span>
            </label>
            <label class="flex items-center gap-2">
              <RadioGroupItem value="custom" />
              <span class="text-sm">Address</span>
            </label>
          </div>
        </RadioGroup>
        <LabeledField label="Radius">
          <Button
            v-if="!hasRadius"
            variant="ghost"
            @click="createRadius">
            <MapPin class="h-4 w-4 mr-1" />
            <span>Add radius</span>
          </Button>
          <div
            v-else
            class="flex items-center gap-2">
            <Input
              v-model.number="bulkAddParams.radius"
              type="number"
              class="w-24"
              :min="getMin"
              :max="80" />
            <Button
              variant="destructive"
              size="icon-sm"
              @click="removeRadius">
              <X class="h-3 w-3" />
            </Button>
            <span class="text-sm">km</span>
          </div>
        </LabeledField>
        <LabeledField label="Locations">
          <Textarea
            v-model="bulkAddParams.locations"
            class="min-h-20 max-h-[50vh]" />
        </LabeledField>
        <Button @click="handleBulkAddLocations">OK</Button>
      </div>
    </DialogContent>
  </Dialog>
</template>
